import axios from 'axios'
import Cookies from 'cookies-js'
import { PROVISIONED_UI_FLAGS } from '@Builder/QuoteDetailPage/logic'

import {
    formatAuthResponse,
    refreshToken,
    setAuth,
    resetAuth,
    setAuthError,
    getMfaStatus,
    setMfaStatus,
    GRAPHQL_API_REST_BASE,
    FICTIV_JWT_COOKIE,
} from '~/redux-modules/auth'
import { trackIdentify } from '~/lib/analytics/actions/identify'
import {
    attributionStartSession,
    attributionEndSession,
} from '~/lib/attributionEvents'
import logger from '~/lib/logger'
import FictivSymbol from '~/lib/FictivSymbol'
import isFlagEnabled from '~/lib/isFlagEnabled'
import { punchoutSessionStorage } from '~/lib/services/WebStorage/PunchoutSessionStorage'
import { setUserContext } from '~/sentry'
import {
    CurrentUserIdentityDocument,
    GetIs2faEnabledMyAccountDocument,
} from '~/gql/graphql'
export const START = FictivSymbol('identity/START')
export const SUCCESS = FictivSymbol('identity/SUCCESS')
export const ERROR = FictivSymbol('identity/ERROR')
export const SET_TOKEN = FictivSymbol('identity/SET_TOKEN')

export const REGISTER_FAIL = 1
export const NETWORK_ERROR = 3

const ANON_USER = Object.freeze({
    id: null,
    email: null,
    name: null,
    firstName: null,
    lastName: null,
    postalCode: null,
    isVendor: false,
    munchkinHash: null,
    loading: true,
    discounts: [],
    token: null,
    flags: {},
    isImpersonating: false,
})

export default (state = ANON_USER, action) => {
    switch (action.type) {
        case SET_TOKEN:
            return {
                ...state,
                token: action.token,
            }
        case START:
            return {
                ...state,
                email: action.email,
                loading: true,
            }
        case SUCCESS:
            return {
                ...state,
                ...action.identity,
                error: undefined,
                loading: false,
            }
        case ERROR:
            return {
                ...state,
                loading: false,
                email: action.email,
                error: action.error,
            }
        default:
            return state
    }
}

// ******* Selectors *******
export function getUserIdentity(state) {
    return state.identity
}

export function getRedirectingDrawingtagData(state) {
    const identity = state.identity

    const selectedQuoteModelRevision =
        state.dfmLayers?.selectedQuoteModelRevisions[0]

    const quote = state.dfmLayers?.quote

    return {
        selectedQuoteModelRevision,
        identity,
        quote,
        isQueryRefactorEnabled: isFlagEnabled(
            identity,
            PROVISIONED_UI_FLAGS.QUOTE_DETAILS_QUERY_REFACTOR,
            'true',
        ),
    }
}

export function getIsVendor(state) {
    const currentUser = getUserIdentity(state)

    return Boolean(currentUser ? currentUser.isVendor : false)
}
export function isImpersonating(state) {
    const currentUser = getUserIdentity(state)

    return Boolean(currentUser && currentUser.isImpersonating)
}

export function isUserHasBusinessAccount(state) {
    const { businessAccountMembership } = getUserIdentity(state)
    const userHasBusinessAccount =
        businessAccountMembership &&
        businessAccountMembership?.businessAccount?.id

    return Boolean(userHasBusinessAccount)
}

export function formatFlags(flags = []) {
    return flags.reduce((prev, { key, variant }) => {
        prev[key] = variant
        return prev
    }, {})
}

export function identifyUser() {
    return async (dispatch, getState, { client }) => {
        dispatch({ type: START, email: undefined })

        try {
            let identity = ANON_USER

            const idToken = await dispatch(refreshToken())

            if (idToken) {
                const userIdentityResponse = await client.query({
                    query: CurrentUserIdentityDocument,
                })

                if (userIdentityResponse?.data?.error) {
                    throw userIdentityResponse.data.error
                }

                const { data: userIdentityData } = userIdentityResponse

                identity = formatAuthResponse(userIdentityData, idToken)
                const enforce2faEnabled = isFlagEnabled(
                    identity,
                    'ENFORCE_2FA',
                    'true',
                )
                const mfaEnabled = getMfaStatus(getState())

                identity = { ...identity, mfaEnabled }

                if (identity.isAdmin && enforce2faEnabled && !mfaEnabled) {
                    try {
                        const { data: userMfastatusData } = await client.query({
                            query: GetIs2faEnabledMyAccountDocument,
                        })

                        identity = {
                            ...identity,
                            mfaEnabled: userMfastatusData?.me?.is2faEnabled,
                        }
                        // set mfa status in auth as well
                        dispatch(
                            setMfaStatus(userMfastatusData?.me?.is2faEnabled),
                        )
                    } catch (error) {
                        // if we failed to get mfaStatus, should not block user login or visit app, but need to log it
                        logger.exception(error, {
                            extra: {
                                info: 'Redux::Identity::Failed to get user is2faEnabled',
                                id: identity.id,
                                email: identity.email,
                            },
                        })
                    }
                }
                dispatch(setAuth({ user: identity, auth: idToken }))

                const { email, id } = identity
                const action = { type: SUCCESS, identity }

                setUserContext({ email, id })
                attributionStartSession(email)
                dispatch(trackIdentify(action, id, identity))
            }
        } catch (err) {
            dispatch(setAuthError(err))
            dispatch({ type: ERROR, error: NETWORK_ERROR })
        }

        return getUserIdentity(getState())
    }
}

// Use for creating test user, need to make sure if it still use it or not
export function registerUser({ email, password }) {
    return async (dispatch, getState, { api }) => {
        const item = { email, password }

        dispatch({ type: START, email })

        try {
            await api.post('/api/v1/identity/session', { data: { item } })
            await dispatch(identifyUser())
        } catch (error) {
            dispatch({
                type: ERROR,
                error: REGISTER_FAIL,
                isWrongPassword: /password mismatch/i.test(error.message),
                email,
            })
        }

        return getUserIdentity(getState())
    }
}

export function signOutUser() {
    return async dispatch => {
        await axios.post(
            `${GRAPHQL_API_REST_BASE}/logout`,
            {},
            {
                withCredentials: true,
            },
        )

        dispatch(resetAuth())

        Cookies.expire(FICTIV_JWT_COOKIE)
        attributionEndSession()
        punchoutSessionStorage.removeItem()
        window.location.assign(`/login${window.location.search}`)
    }
}

export const isTeamsFeatureEnabled = state => {
    const { businessAccountMembership } = getUserIdentity(state)

    return !!businessAccountMembership?.businessAccount?.isTeamsFeatureEnabled
}
